import { unwrapResult } from '@reduxjs/toolkit';
import { LayoutPublic } from 'components/layout';
import { ButtonCTA, Checkbox, SpinnerSmall, TextField } from 'components/ui';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAppDispatch } from 'store';
import { login, userDetailsState } from 'store/userDetailsSlice';
import { usePageTitle } from 'utils';

export default function LoginPage() {
  usePageTitle('Login | Segments');

  /** @type {React.MutableRefObject<null> } */
  const form = useRef(null);

  const dispatch = useAppDispatch();

  const { loading } = useSelector(userDetailsState);
  const [errorMsg, setErrorMsg] = useState('');

  /** @param {*} event */
  function handleOnSubmit(event) {
    event.preventDefault();
    const {
      email,
      password,
      // rememberMe
    } = event.target;
    const formState = {
      email: email.value,
      password: password.value,
      rememberMe: true, // keep user session until backend login refactoring to session token
      // rememberMe: rememberMe.checked,
    };
    dispatch(login(formState))
      .then(unwrapResult)
      .catch((err) => {
        toast.error(
          'The email or password you entered is incorrect. Try again.',
          {
            autoClose: 10000,
          }
        );
      });
  }

  useEffect(() => {
    const inputs = document.querySelectorAll('input');

    inputs.forEach((input) => {
      input.addEventListener(
        'invalid',
        () => {
          input.classList.add(
            'focus:ring-segments-at-risk',
            'focus:border-segments-at-risk'
          );
        },
        false
      );

      input.addEventListener('keyup', (e) => {
        const capslock = e.getModifierState?.('CapsLock');
        if (capslock) {
          setErrorMsg('Note : Your Capslock key is currently turned on');
        } else {
          setErrorMsg('');
        }
      });
    });
  }, []);

  return (
    <LayoutPublic>
      <div className="px-5 pt-5 md:pt-24 lg:px-32">
        <div className="flex items-center -ml-1 lg:ml-0">
          <img
            width="40"
            height="40"
            src="/assets/logo-segments.png"
            alt="logo"
          />
          <h1 className="ml-1 text-base font-normal leading-7 md:text-2xl md:ml-3 tracking-segments_logo text-segments-dark">
            Segments
          </h1>
        </div>

        <div className="flex justify-center mx-auto md:w-segments-411px">
          <form
            ref={form}
            onSubmit={handleOnSubmit}
            className="w-full mt-12 text-2xl font-bold leading-9 lg:mt-14"
          >
            <h1>Log in to Segments</h1>

            <div className="mt-9 lg:mt-12">
              <label htmlFor="email" className="block text-sm font-bold">
                Email
              </label>
              <TextField
                // @ts-ignore
                name="email"
                type="email"
                placeholder="Required"
                required
              />
              {errorMsg && (
                <p className="text-segments-13px text-segments-at-risk">
                  {errorMsg}
                </p>
              )}
            </div>
            <div className="mt-6">
              <label htmlFor="password" className="block text-sm font-bold">
                Password
              </label>
              <TextField
                // @ts-ignore
                name="password"
                type="password"
                placeholder="Required"
                minLength={5}
                maxLength={128}
                required
              />
            </div>
            <div className="flex items-center justify-between mt-8">
              <label htmlFor="rememberMe" className="flex items-center">
                <Checkbox name="rememberMe" />
                <span className="font-normal leading-4 text-segments-13px">
                  keep me logged in
                </span>
              </label>
              <Link
                className="font-normal leading-4 text-segments-13px"
                to="/account/reset-password"
              >
                Forgot password{' '}
              </Link>
            </div>
            <div className="items-center justify-between mt-12 lg:flex ">
              <p className="font-normal leading-4 text-segments-13px">
                or{' '}
                <Link to="/register" className="text-segments-link">
                  sign up
                </Link>{' '}
                for a new account
              </p>

              <div className="w-full mt-8 lg:mt-0 lg:w-32">
                <ButtonCTA
                  type="submit"
                  variant="main"
                  disabled={loading}
                  cursorNotAllowed={loading}
                >
                  {loading ? <SpinnerSmall /> : 'Log in'}
                </ButtonCTA>
              </div>
            </div>

            <div className="flex justify-center py-32">
              <a href="https://apps.shopify.com/segments">
                <img
                  width="332"
                  height="68"
                  src="/assets/Shopify-App-Store-Badge-Final-Black.svg"
                  alt="Shopify App Store"
                />
              </a>
            </div>
          </form>
        </div>
      </div>
    </LayoutPublic>
  );
}
